import { type TrafficLightType } from '@scaleaq/scaleui';
import { DeviceStatus, StatusLevel } from '@scaleaq/sitecontrol-lib';

import { WidgetHealthIssue, WidgetHealthSeverity } from '@/types.ts';

const statusLevelToWidgetHealthSeverity = (level: StatusLevel): WidgetHealthSeverity => {
  switch (level) {
    case StatusLevel.Critical:
      return 'critical';
    case StatusLevel.Error:
      return 'error';
    case StatusLevel.Warning:
      return 'warning';
    case StatusLevel.Healthy:
      return 'ok';
    default:
      return 'error';
  }
};

const widgetHealthSeverityPriority: Record<WidgetHealthSeverity, number> = {
  critical: 0,
  error: 1,
  warning: 2,
  ok: 3,
};

export const widgetHealthSeverityToTrafficLightType: Record<
  WidgetHealthSeverity,
  TrafficLightType
> = {
  critical: 'critical',
  error: 'alarm',
  warning: 'caution',
  ok: 'success',
};

export const deviceStatusesToWidgetHealthIssues = (statuses: DeviceStatus[]): WidgetHealthIssue[] =>
  statuses
    .filter(status => status.level !== StatusLevel.Healthy && status.message?.trim())
    .map(status => ({
      severity: statusLevelToWidgetHealthSeverity(status.level),
      message: status.message,
    }));

/**
 * Returns undefined (not []) when there is nothing to report, so callers can
 * fall through to other issue sources with `??`.
 *
 * `fallbackMessage` covers devices that report a non-healthy level without a
 * message; without it such a status would be silently dropped.
 */
export const deviceStatusToWidgetHealthIssues = (
  status?: DeviceStatus,
  fallbackMessage?: string
): WidgetHealthIssue[] | undefined => {
  if (!status || status.level === StatusLevel.Healthy) return undefined;

  const message = status.message?.trim() ? status.message : fallbackMessage?.trim();
  if (!message) return undefined;

  return [{ severity: statusLevelToWidgetHealthSeverity(status.level), message }];
};

export const sortWidgetHealthIssuesByPriority = (
  issues: WidgetHealthIssue[]
): WidgetHealthIssue[] => {
  return issues
    .map((issue, index) => ({ issue, index }))
    .sort((a, b) => {
      const priorityDiff =
        widgetHealthSeverityPriority[a.issue.severity] -
        widgetHealthSeverityPriority[b.issue.severity];

      // Keep input order for issues with the same severity.
      if (priorityDiff === 0) return a.index - b.index;
      return priorityDiff;
    })
    .map(({ issue }) => issue);
};
